/*
 * DO NOT EDIT
 * ===========
 * This file was auto-generated.
 * To update run the TypeScript Code Generator in the API.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ORGANISATION_PLACEHOLDER,
  REGIME_PLACEHOLDER,
  REGISTER_PLACEHOLDER,
} from '@assetaccountant/core/http-token.interceptor';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { OperationResponse } from '../models/generic/operation-response';
import { ReportTemplateFull } from '../models/report-template-full';
import { ReportTemplateIdSet } from '../models/report-template-id-set';
import { ReportTemplateListItem } from '../models/report-template-list-item';
import { ReportTemplatePublishedSet } from '../models/report-template-published-set';
import { ConsolidatedReportRestrictionQuery } from '../models/reports/consolidated-report-restriction.query';
import { CreateReportTemplateApiCommand } from '../models/reports/create-report-template-api.command';
import { DownloadOrganisationReportQuery } from '../models/reports/download-organisation-report.query';
import { EditReportTemplateApiCommand } from '../models/reports/edit-report-template-api.command';
import { GenerateAcquisitionsReportCommand } from '../models/reports/generate-acquisitions-report.command';
import { GenerateAssetExportReportCommand } from '../models/reports/generate-asset-export-report.command';
import { GenerateAssetGroupSummaryReportCommand } from '../models/reports/generate-asset-group-summary-report.command';
import { GenerateAssetScheduleReportCommand } from '../models/reports/generate-asset-schedule-report.command';
import { GenerateAssetSummaryReportCommand } from '../models/reports/generate-asset-summary-report.command';
import { GenerateClassificationSplitReportCommand } from '../models/reports/generate-classification-split-report.command';
import { GenerateConsolidatedAssetExportReportCommand } from '../models/reports/generate-consolidated-asset-export-report.command';
import { GenerateConsolidatedAssetGroupSummaryReportCommand } from '../models/reports/generate-consolidated-asset-group-summary-report.command';
import { GenerateConsolidatedDepreciationForecastReportCommand } from '../models/reports/generate-consolidated-depreciation-forecast-report.command';
import { GenerateConsolidatedTaxAutomateReportCommand } from '../models/reports/generate-consolidated-tax-automate-report.command';
import { GenerateCustomReportTemplateCommand } from '../models/reports/generate-custom-report-template.command';
import { GenerateDepreciationForecastReportCommand } from '../models/reports/generate-depreciation-forecast-report.command';
import { GenerateDisposalsReportCommand } from '../models/reports/generate-disposals-report.command';
import { GenerateImpairmentReportCommand } from '../models/reports/generate-impairment-report.command';
import { GenerateRevaluationMovementsReportCommand } from '../models/reports/generate-revaluation-movements-report.command';
import { GenerateTaxAuditReportCommand } from '../models/reports/generate-tax-audit-report.command';
import { GenerateTaxAutomateReportCommand } from '../models/reports/generate-tax-automate-report.command';
import { ListReportStatusResponse } from '../models/reports/list-report-status.response';
import { ListReportTemplateApiQuery } from '../models/reports/list-report-template-api.query';
import { PublishReportTemplateApiCommand } from '../models/reports/publish-report-template-api.command';
import { PublishedCheckReportTemplateApiCommand } from '../models/reports/published-check-report-template-api.command';
import { QueueReportResponse } from '../models/reports/queue-report.response';
import { ReportRestrictionResponse } from '../models/reports/report-restriction.response';
import { UnpublishReportTemplateApiCommand } from '../models/reports/unpublish-report-template-api.command';

@Injectable({
  providedIn: 'root',
})
export class ReportApi {
  constructor(private http: HttpClient) {}

  checkConsolidatedReportRestrictions(
    command: ConsolidatedReportRestrictionQuery,
  ): Observable<ReportRestrictionResponse> {
    return this.http.post<ReportRestrictionResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/report-restrictions`,
      command,
    );
  }

  checkReportRestrictions(registerId?: number | null): Observable<ReportRestrictionResponse> {
    return this.http.get<ReportRestrictionResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${registerId ?? REGISTER_PLACEHOLDER}/report-restrictions`,
    );
  }

  createReportTemplate(
    command: CreateReportTemplateApiCommand,
  ): Observable<OperationResponse<ReportTemplateIdSet>> {
    return this.http.post<OperationResponse<ReportTemplateIdSet>>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates`,
      command,
    );
  }

  disableReportTemplate(reportTemplateId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${reportTemplateId}/disable`,
      {},
    );
  }

  downloadReport(query: DownloadOrganisationReportQuery): Observable<Blob> {
    return this.http.get(
      `${environment.servicesApiUrl}/api/v1/reports/organisations/${query.organisationId ?? ORGANISATION_PLACEHOLDER}/requests/${query.requestId}`,
      { responseType: 'blob' },
    );
  }

  enableReportTemplate(reportTemplateId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${reportTemplateId}/enable`,
      {},
    );
  }

  executeReportTemplate(command: GenerateCustomReportTemplateCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${command.reportTemplateId}/execute`,
      command,
      { responseType: 'blob' },
    );
  }

  generateAcquisitionsReport(command: GenerateAcquisitionsReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/acquisitions`,
      command,
      { responseType: 'blob' },
    );
  }

  generateAssetExportReport(command: GenerateAssetExportReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-export`,
      command,
      { responseType: 'blob' },
    );
  }

  generateAssetGroupSummaryReport(
    command: GenerateAssetGroupSummaryReportCommand,
  ): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-group-summary`,
      command,
      { responseType: 'blob' },
    );
  }

  generateAssetScheduleReport(command: GenerateAssetScheduleReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-schedule`,
      command,
      { responseType: 'blob' },
    );
  }

  generateAssetSummaryReport(command: GenerateAssetSummaryReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-summary`,
      command,
      { responseType: 'blob' },
    );
  }

  generateClassificationSplitReport(
    command: GenerateClassificationSplitReportCommand,
  ): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/classification-split-report`,
      command,
      { responseType: 'blob' },
    );
  }

  generateConsolidatedAssetExportReport(
    command: GenerateConsolidatedAssetExportReportCommand,
  ): Observable<QueueReportResponse> {
    return this.http.post<QueueReportResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-export`,
      command,
    );
  }

  generateConsolidatedAssetGroupSummaryReport(
    command: GenerateConsolidatedAssetGroupSummaryReportCommand,
  ): Observable<QueueReportResponse> {
    return this.http.post<QueueReportResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/asset-group-summary`,
      command,
    );
  }

  generateConsolidatedDepreciationForecastReport(
    command: GenerateConsolidatedDepreciationForecastReportCommand,
  ): Observable<QueueReportResponse> {
    return this.http.post<QueueReportResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/depreciation-forecast`,
      command,
    );
  }

  generateConsolidatedTaxAutomateReport(
    command: GenerateConsolidatedTaxAutomateReportCommand,
  ): Observable<QueueReportResponse> {
    return this.http.post<QueueReportResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/tax-automate`,
      command,
    );
  }

  generateDepreciationForecastReport(
    command: GenerateDepreciationForecastReportCommand,
  ): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/depreciation-forecast`,
      command,
      { responseType: 'blob' },
    );
  }

  generateDisposalsReport(command: GenerateDisposalsReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/disposals`,
      command,
      { responseType: 'blob' },
    );
  }

  generateImpairmentReport(command: GenerateImpairmentReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/impairment-report`,
      command,
      { responseType: 'blob' },
    );
  }

  generateRevaluationMovementsReport(
    command: GenerateRevaluationMovementsReportCommand,
  ): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/revaluation-movements-report`,
      command,
      { responseType: 'blob' },
    );
  }

  generateTaxAuditReport(command: GenerateTaxAuditReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/tax-audit`,
      command,
      { responseType: 'blob' },
    );
  }

  generateTaxAutomateReport(command: GenerateTaxAutomateReportCommand): Observable<Blob> {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${command.registerId ?? REGISTER_PLACEHOLDER}/regimes/${command.regimeId ?? REGIME_PLACEHOLDER}/tax-automate`,
      command,
      { responseType: 'blob' },
    );
  }

  getReportTemplate(reportTemplateId: number): Observable<OperationResponse<ReportTemplateFull>> {
    return this.http.get<OperationResponse<ReportTemplateFull>>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${reportTemplateId}`,
    );
  }

  listOrganisationReports(organisationId?: number | null): Observable<ListReportStatusResponse> {
    return this.http.get<ListReportStatusResponse>(
      `${environment.servicesApiUrl}/api/v1/reports/organisations/${organisationId ?? ORGANISATION_PLACEHOLDER}`,
    );
  }

  listReportTemplates(
    query: ListReportTemplateApiQuery = {},
  ): Observable<OperationResponse<ReportTemplateListItem[]>> {
    return this.http.get<OperationResponse<ReportTemplateListItem[]>>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates`,
      {
        params: {
          ...(query.showDisabled ? { showDisabled: query.showDisabled } : {}),
          ...(query.take ? { take: query.take } : {}),
          ...(query.skip ? { skip: query.skip } : {}),
        },
      },
    );
  }

  listReportTemplatesInRegister(
    query: ListReportTemplateApiQuery = {},
  ): Observable<OperationResponse<ReportTemplateListItem[]>> {
    return this.http.get<OperationResponse<ReportTemplateListItem[]>>(
      `${environment.servicesApiUrl}/api/v1/reports/registers/${query.registerId ?? REGISTER_PLACEHOLDER}/report-templates`,
      {
        params: {
          ...(query.showDisabled ? { showDisabled: query.showDisabled } : {}),
          ...(query.take ? { take: query.take } : {}),
          ...(query.skip ? { skip: query.skip } : {}),
        },
      },
    );
  }

  publishedCheckReportTemplate(
    command: PublishedCheckReportTemplateApiCommand,
  ): Observable<OperationResponse<ReportTemplatePublishedSet>> {
    return this.http.post<OperationResponse<ReportTemplatePublishedSet>>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${command.reportTemplateId}/published`,
      command,
    );
  }

  publishReportTemplate(command: PublishReportTemplateApiCommand): Observable<void> {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${command.reportTemplateId}/publish`,
      command,
    );
  }

  saveReportTemplate(command: EditReportTemplateApiCommand): Observable<void> {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${command.reportTemplateId}`,
      command,
    );
  }

  unpublishReportTemplate(command: UnpublishReportTemplateApiCommand): Observable<void> {
    return this.http.delete<void>(
      `${environment.servicesApiUrl}/api/v1/reports/report-templates/${command.reportTemplateId}/unpublish`,
      { body: command },
    );
  }
}
